//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { useContext, useEffect } from 'react';
// import { useState, useEffect, useContext, useCallback } from 'react';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { Grid, makeStyles } from '@material-ui/core';
import { useHistory } from 'react-router-dom';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
import { logPageView } from '../../system/firebase';
import { PATH_HOME } from '../../system/constants';
// import { unsubscribeRealtimeUpdates } from '../../system/firebase';
import { LayoutContext } from '../../common/contexts';
import { setClassNameInDefns } from '../../common/definitionHelper';
import { HOMEPAGE_CONTACT, PAGE_TITLE_ABOUT_US, PAGE_TITLE_ABOUT_US_SEO } from '../../common/constants';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------
import aboutImage1 from '../../images/home/home_about_img2.png';
import aboutImage2 from '../../images/home/home_about_img1.png';
import aboutBanner from '../../images/Banner/Home_About.jpg';
import circleBottom from '../../images/Banner/circle-bottom.png';
import circle from '../../images/Banner/circle-left.png';

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
import AfitdLink from '../../common/elemental/AfitdLink';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------
import AfitdPageHead from '../../common/elemental/AfitdPageHead';
import Footer from './Footer';

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
// import cx from 'clsx';
// import _get from 'lodash/get';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const useStyles = makeStyles(theme => ({
    root: {
        overflowX: 'hidden',
    },
    aboutContainer: { padding: 100, [theme.breakpoints.down('md')]: { padding: 40 }, [theme.breakpoints.down('sm')]: { padding: 16 }, maxWidth: 1200, margin: 'auto' },
    imageContainer: { textAlign: 'center', marginTop: '50px' },
    secondaryHeading: {
        fontSize: '42px',
        fontWeight: 700,
        lineHeight: '52px',
        [theme.breakpoints.down('md')]: { fontSize: '36px', lineHeight: '46px' },
        [theme.breakpoints.down('sm')]: { fontSize: '24px', textAlign: 'center' },
    },
    paragraphContainer: { maxWidth: '450px', [theme.breakpoints.down('sm')]: { textAlign: 'center', margin: 'auto' } },
    paragraph: { fontSize: '20px', fontWeight: 400, lineHeight: '34px', [theme.breakpoints.down('sm')]: { fontSize: 18, lineHeight: '28px' } },
    aboutSection: { display: 'flex', flexDirection: 'column', justifyContent: 'center' },
    aboutSection2: { display: 'flex', flexDirection: 'column', justifyContent: 'center', paddingLeft: 100, [theme.breakpoints.down('md')]: { paddingLeft: 30 } },
    contactUsLink: { color: '#1d8a8b', fontWeight: 'bold', textDecoration: 'underline', marginRight: 6 },
    bannerContainer: {
        backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)),url('${aboutBanner}')`,
        height: '710px',
        backgroundPosition: 'center',
        backgroundSize: 'cover',
        position: 'relative',
        [theme.breakpoints.down('lg')]: { justifyContent: 'center' },
        [theme.breakpoints.down('md')]: { height: 490 },
        // [theme.breakpoints.down('sm')]: { height: 360 },
    },
    bannerContent1: { width: 280 },
    bannerContent2: { paddingTop: 115, [theme.breakpoints.down('md')]: { paddingTop: 0 }, [theme.breakpoints.down('sm')]: { textAlign: 'center', paddingTop: 0 } },
    bannerHeading: { fontSize: 54, color: 'white', [theme.breakpoints.down('md')]: { fontSize: 42 }, [theme.breakpoints.down('sm')]: { fontSize: 24 } },
    bannerParagraph: {
        fontSize: 18,
        color: 'rgba(255, 255, 255, 0.8)',
        paddingTop: 16,
        [theme.breakpoints.down('lg')]: { paddingTop: 24 },
        [theme.breakpoints.down('md')]: { paddingTop: 0 },
        [theme.breakpoints.down('sm')]: { fontSize: 16 },
    },
    bannerImageBottomContainer: { position: 'absolute', bottom: -4, right: 24 },
    circleImageBottom: {},
    circleImage: { paddingTop: 136 },

    aboutUsPara: { paddingTop: 20, fontWeight: 'bold', fontSize: 20, color: 'white' },
    aboutUsContainer: { fontFamily: 'Roboto' },
    aboutUsSidebar: { width: 320, paddingTop: 20 },
    aboutUsProfile: { padding: 20 },
    AboutUsProfileText: { fontSize: 16, fontWeight: 400 },
    sidebarHeading: { fontWeight: 500, fontSize: 18 },
    sidebarItem: {},
    img: { width: '100%' },
    img1: { width: '100%', [theme.breakpoints.down('md')]: { transform: 'translateY(100px)' }, [theme.breakpoints.down('sm')]: { transform: 'translateY(0px)' } },
    haveOtherQuestionsContainer: {
        padding: 100,
        [theme.breakpoints.down('md')]: { padding: 40 },
        [theme.breakpoints.down('sm')]: { padding: 16 },
        maxWidth: 1200,
        textAlign: 'center',
        marginLeft: 'auto',
        marginRight: 'auto',
        marginTop: 90,
        marginBottom: 0,
    },
    versionContainer: {
        padding: '0 100px 20px 100px',
        color: 'gray',
        fontSize: 12,
        [theme.breakpoints.down('md')]: { padding: 20 },
        [theme.breakpoints.down('sm')]: { padding: 16 },
        maxWidth: 1200,
        textAlign: 'center',
        marginLeft: 'auto',
        marginRight: 'auto',
        marginTop: 0,
        marginBottom: 0,
    },
}));

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const { classes } = params;

    const defns = {
        aboutContainer: { container: true },
        haveOtherQuestionsContainer: {},
        versionContainer: {},
        aboutSection: { item: true, lg: 6, md: 6, sm: 12, xs: 12 },
        aboutSection2: { item: true, lg: 6, md: 6, sm: 12, xs: 12 },
        imageContainer: {},
        secondaryHeading: {},
        paragraph: {},
        paragraphContainer: {},
        contactUsLink: {},
        bannerContainer: { container: true, direction: 'row' },
        bannerContent1: { item: true, xl: 2, lg: 2, md: 2, sm: 2, xs: 2 },
        bannerContent2: { item: true, xl: 8, lg: 11, md: 10, sm: 11, xs: 11 },
        bannerContent3: { item: true, xl: 2, lg: 11, md: 10, sm: 11, xs: 11 },
        bannerHeading: {},
        bannerParagraph: {},
        circleImage: {},

        aboutUsPara: {},
        aboutUsContainer: { container: true, direction: 'row' },
        aboutUsSidebar: { item: true, md: 3, sm: 3, xs: 3 },
        aboutUsProfile: { item: true, md: 9, sm: 9, xs: 9 },
        AboutUsProfileText: {},
        sidebarHeading: {},
        sidebarItem: {},
    };

    // Automatically inject className for matching keys
    setClassNameInDefns(defns, classes);

    return defns;
};

//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
const AboutUs = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const classes = useStyles();

    const theme = useTheme();
    const matchesSm = useMediaQuery(theme.breakpoints.down('sm'));
    const matchesXl = useMediaQuery(theme.breakpoints.up('xl'));
    const matchesLgUp = useMediaQuery(theme.breakpoints.up('lg'));
    // const lgMatches = useMediaQuery(theme.breakpoints.down('lg'));

    // console.log(matchesSm);
    // const appContext = useContext(AppContext);
    // const { currentUser, userId, profileData, currentEmployer, employerId, lovs } = appContext;

    const layoutContext = useContext(LayoutContext);
    const { scrollToPageTop } = layoutContext;
    // const { setSnackbarMessage, setSnackbarErrorMessage } = layoutContext;

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------
    // const { propExample } = props;
    let history = useHistory();

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    // const [isDirty, setIsDirty] = useState(false);

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------
    useEffect(() => {
        logPageView(PAGE_TITLE_ABOUT_US);
    }, []);

    // on mount, scroll to the top of the page
    useEffect(() => {
        scrollToPageTop();
    }, [scrollToPageTop]);

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // useCallback / Event handlers
    //---------------------------------------------------------------------
    const handleContactUsLinkClick = () => {
        history.push(`${PATH_HOME}/${HOMEPAGE_CONTACT}`);
    };

    //---------------------------------------------------------------------
    // Definitions
    //---------------------------------------------------------------------
    const defns = getDefns({ classes, props });

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return (
        <div {...defns.root}>
            <AfitdPageHead
                title={PAGE_TITLE_ABOUT_US_SEO}
                description="A Foot in the Door is revolutionizing the job search experience by providing the latest tools and resources to help job seekers with 0-5 years of work experience."
                // keywords=""
                // canonicalUrl={`https://${AFITD_DOMAIN}${PATH_HOME}/${HOMEPAGE_ABOUT}`}
            />
            {/* <h1 >Hello About Banner Here</h1> */}

            {/* banner section start */}
            <Grid {...defns.bannerContainer}>
                {matchesXl ? (
                    <Grid {...defns.bannerContent1}>
                        <img {...defns.circleImage} src={circle} alt="" />
                    </Grid>
                ) : null}

                <Grid {...defns.bannerContent2}>
                    <h1 {...defns.bannerHeading}>
                        Our Job Is To <span style={{ textDecoration: 'underline' }}>Help You</span> Become {matchesLgUp && <br />}
                        Who You Are Meant To Be!
                    </h1>
                    {matchesXl && <p {...defns.aboutUsPara}>ABOUT US</p>}

                    <p {...defns.bannerParagraph}>
                        <strong>A Foot in the Door</strong> is revolutionizing every aspect of your job search experience because the world is never going back to how it used to
                        be. Now is the time to become better. That’s why we stop at nothing to help job seekers with 0-5 years of work experience get seen and hired by the best
                        companies in the world. Utilize video, social media, and epic profile boosting features so you can get paid for your talent, skills, and the superpowers
                        that make you who you are.
                        <br />
                        <br />
                        Say hello to your future. It is very bright and it’s happening here and now...
                        {/* {lgMatches ? null : <br />} */}
                    </p>
                    {matchesXl
                        ? null
                        : matchesLgUp && (
                              <Grid {...defns.bannerImageBottomContainer}>
                                  <img {...defns.circleImageBottom} src={circleBottom} alt="" />
                              </Grid>
                          )}
                </Grid>
                {matchesXl ? <Grid {...defns.bannerContent3}></Grid> : null}
            </Grid>
            {/* banner section end */}

            <Grid {...defns.aboutContainer}>
                <Grid {...defns.aboutSection}>
                    <h2 {...defns.secondaryHeading}>
                        Not your dad’s job <br /> search site
                    </h2>
                    <div {...defns.paragraphContainer}>
                        <p {...defns.paragraph}>
                            Sound familiar? You’re scrolling that rickety job search site. Fumbling through fifty two job listings. Reading a gazillion job descriptions. Oh look, a
                            cool one catches your eye. You’re pumped to apply. But wait. Ugh, not again. The years of experience required for the job are higher than the number of
                            years you’ve been alive!
                        </p>
                        <p {...defns.paragraph}>
                            Entry level job seekers need the most support, yet they are the most vulnerable community in the job market and we say that’s unacceptable! So we’re
                            shaking things up. Here, every job posted is designed for job seekers with 0-5 years of work experience. In a single click you can apply to jobs,
                            schedule interviews, get real-time updates on your job search, and even conduct secure virtual interviews inside of our platform.
                        </p>
                    </div>
                </Grid>
                <Grid {...defns.aboutSection} {...defns.imageContainer}>
                    <img
                        {...defns.img1}
                        style={{
                            maxWidth: 435,
                            paddingLeft: 20,
                        }}
                        src={aboutImage1}
                        alt=""
                    />
                </Grid>
            </Grid>
            {matchesSm ? (
                <Grid {...defns.aboutContainer}>
                    <Grid {...defns.aboutSection}>
                        <h2 {...defns.secondaryHeading}>
                            Interviewing <br /> made easy
                        </h2>
                        <div {...defns.paragraphContainer}>
                            <p {...defns.paragraph}>
                                Interviews focus on your future. So we’re modernizing the way you get them done. One click is all that is required! Virtual interviews save money,
                                time from traveling to and fro, and we keep your connection secure so you can simply discuss the important stuff.
                            </p>
                        </div>
                    </Grid>
                    <Grid {...defns.aboutSection} {...defns.imageContainer}>
                        <img {...defns.img} style={{ maxWidth: 490 }} src={aboutImage2} alt="" />
                    </Grid>
                </Grid>
            ) : (
                <Grid {...defns.aboutContainer}>
                    <Grid {...defns.aboutSection} {...defns.imageContainer}>
                        <img {...defns.img} style={{ maxWidth: 490, width: '100%' }} src={aboutImage2} alt="" />
                    </Grid>
                    <Grid {...defns.aboutSection2}>
                        <h2 {...defns.secondaryHeading}>
                            Interviewing <br /> made easy
                        </h2>
                        <div {...defns.paragraphContainer}>
                            <p {...defns.paragraph}>
                                Interviews focus on your future. So we’re modernizing the way you get them done. One click is all that is required! Virtual interviews save money,
                                time from traveling to and fro, and we keep your connection secure so you can simply discuss the important stuff.
                            </p>
                        </div>
                    </Grid>
                </Grid>
            )}

            <div {...defns.haveOtherQuestionsContainer}>
                <h2 {...defns.secondaryHeading}>Have other questions?</h2>
                <p {...defns.paragraph}>
                    <AfitdLink {...defns.contactUsLink} onClick={handleContactUsLinkClick}>
                        Contact Us
                    </AfitdLink>
                    here so we can help.
                </p>
            </div>

            <div {...defns.versionContainer}>
                {`A Foot in the Door v${process.env.REACT_APP_MAJOR_VERSION}.${process.env.REACT_APP_MINOR_VERSION}.${process.env.REACT_APP_PATCH_VERSION}`}{' '}
                {`${process.env.REACT_APP_ENV === 'PROD' ? '' : `Build ${process.env.REACT_APP_BUILD_VERSION} (${process.env.REACT_APP_ENV})`}`}
            </div>

            <Footer />
        </div>
    );
};

export default AboutUs;
